// src/components/dashboard/DashboardHeader.jsx
import React from "react";
import { Link } from "react-router-dom";

/**
 * Header unifié pour tous les dashboards Artkiv
 * Design élégant façon Artsy avec stats et actions
 * 
 * @param {string} title - Titre principal du dashboard
 * @param {string} subtitle - Sous-titre optionnel
 * @param {Array} stats - Tableau de statistiques [{label, value, icon?, trend?, color?, sublabel?}]
 * @param {Array} actions - Boutons d'action [{label, onClick?, to?, variant?, icon?, disabled?}]
 * @param {ReactNode} customContent - Contenu personnalisé à droite du titre
 */
export default function DashboardHeader({ 
  title, 
  subtitle, 
  stats = [], 
  actions = [],
  customContent 
}) {
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Titre et actions */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          <div className="flex-1">
            <h1 className="text-3xl font-light tracking-tight text-gray-900">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-2 text-base text-gray-600 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>

          {/* Actions ou contenu personnalisé */}
          {customContent ? (
            <div className="flex-shrink-0">
              {customContent}
            </div>
          ) : actions.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {actions.map((action, idx) => (
                <ActionButton key={idx} {...action} />
              ))}
            </div>
          )}
        </div>

        {/* Statistiques */}
        {stats.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat, idx) => (
              <StatCard key={idx} {...stat} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Composant carte statistique
function StatCard({ label, value, icon, trend, color = "gray", sublabel }) {
  const colorClasses = {
    gray: "bg-gray-50 border-gray-200",
    blue: "bg-blue-50 border-blue-200",
    green: "bg-green-50 border-green-200",
    amber: "bg-amber-50 border-amber-200",
    indigo: "bg-indigo-50 border-indigo-200",
    red: "bg-red-50 border-red-200",
    black: "bg-black border-black text-white",
  };

  const iconColorClasses = {
    gray: "text-gray-600",
    blue: "text-blue-600",
    green: "text-green-600",
    amber: "text-amber-600",
    indigo: "text-indigo-600",
    red: "text-red-600",
    black: "text-white opacity-80",
  };

  const textColorClasses = {
    gray: "text-gray-600",
    blue: "text-gray-600",
    green: "text-gray-600",
    amber: "text-gray-600",
    indigo: "text-gray-600",
    red: "text-gray-600",
    black: "text-white opacity-80",
  };

  const valueColorClasses = {
    gray: "text-black",
    blue: "text-black",
    green: "text-black",
    amber: "text-black",
    indigo: "text-black",
    red: "text-black",
    black: "text-white",
  };

  return (
    <div className={`${colorClasses[color]} border rounded-lg p-5 transition-all duration-300 hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={`text-sm font-medium mb-1 ${textColorClasses[color]}`}>
            {label}
          </p>
          <p className={`text-2xl font-light ${valueColorClasses[color]}`}>
            {value}
          </p>
          {sublabel && (
            <p className={`text-xs mt-1 ${color === 'black' ? 'text-white opacity-70' : 'text-gray-500'}`}>
              {sublabel}
            </p>
          )}
          {trend && (
            <p className={`text-xs mt-2 flex items-center gap-1 ${
              trend.direction === 'up' ? 'text-green-600' : 
              trend.direction === 'down' ? 'text-red-600' : 
              color === 'black' ? 'text-white opacity-70' : 'text-gray-600'
            }`}>
              {trend.direction === 'up' && '↑'}
              {trend.direction === 'down' && '↓'}
              {trend.value}
            </p>
          )}
        </div>
        {icon && (
          <div className={`${iconColorClasses[color]}`}>
            {renderIcon(icon)}
          </div>
        )}
      </div>
    </div>
  );
}

// Fonction pour rendre les icônes SVG
function renderIcon(iconName) {
  const icons = {
    // Icône collection
    collection: (
      <svg aria-hidden="true" focusable="false" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    // Icône valeur
    value: (
      <svg aria-hidden="true" focusable="false" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    // Icône estimation
    estimate: (
      <svg aria-hidden="true" focusable="false" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    // Icône tendance haussière
    trending: (
      <svg aria-hidden="true" focusable="false" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    // Icône utilisateurs
    users: (
      <svg aria-hidden="true" focusable="false" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  };

  return icons[iconName] || icons.collection;
}

// Composant bouton d'action
function ActionButton({ label, onClick, to, variant = "primary", icon, disabled }) {
  const baseClasses = "inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const variantClasses = {
    primary: "bg-black text-white hover:bg-gray-800 shadow-sm",
    secondary: "bg-white text-black border border-gray-300 hover:bg-gray-50",
    ghost: "text-gray-700 hover:bg-gray-100",
    danger: "bg-red-600 text-white hover:bg-red-700",
  };

  const classes = `${baseClasses} ${variantClasses[variant]}`;

  const iconSvg = icon && renderActionIcon(icon);

  const content = (
    <>
      {iconSvg}
      {label}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={classes} disabled={disabled} aria-label={typeof label === "string" ? label : undefined}>
      {content}
    </button>
  );
}

// Icônes pour les boutons d'action
function renderActionIcon(iconName) {
  const icons = {
    plus: (
      <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
    edit: (
      <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    share: (
      <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    ),
    download: (
      <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    ),
  };

  return icons[iconName] || null;
}